"use client";

import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import type { SelectedAddon } from "@/features/addons/lib/aggregate-selected-addons";
import { DietAddonSelectedItem } from "./diet-addon-selected-item";

// "Twoje dodatki" heading with the selection count rendered as a filled badge
// next to the label, matching the design.
const SelectedHeading = ({ count }: { count: number }) => {
  const t = useTranslations();

  return (
    <div className="flex items-center gap-1">
      <p className="font-medium text-brand-green2-300 text-lg">
        {t("cart.configuration.addons.summary.selectedTitle")}
      </p>
      <span className="flex size-6 items-center justify-center rounded-full bg-brand-green2-300 font-medium text-brand-white text-sm">
        {count}
      </span>
    </div>
  );
};

type DietAddonsSelectedListProps = {
  items: SelectedAddon[];
  // Seller shown on rows whose addon names none (MPL-3832).
  cateringName?: string;
  // Desktop shows the "Twoje dodatki (N)" header with a remove-all action; on
  // mobile the header lives in the tab bar, so it is hidden here.
  showHeader: boolean;
  // Empty-state helper text differs between desktop and mobile layouts.
  emptyDescription: string;
  onEdit: (addonId: string) => void;
  onRemove: (addonId: string) => void;
  onRemoveAll: () => void;
};

export const DietAddonsSelectedList = ({
  items,
  cateringName,
  showHeader,
  emptyDescription,
  onEdit,
  onRemove,
  onRemoveAll,
}: DietAddonsSelectedListProps) => {
  const t = useTranslations();

  if (items.length === 0) {
    return (
      <div className="flex flex-1 flex-col">
        {showHeader && (
          <div className="mb-4">
            <SelectedHeading count={0} />
          </div>
        )}
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl px-6 py-12 text-center">
          <div className="flex size-14 items-center justify-center rounded-full bg-brand-white text-brand-green2-300">
            <IconPlus className="size-6" stroke={2} />
          </div>
          <div className="flex flex-col gap-1">
            <p className="font-medium text-brand-green2-300 text-lg">
              {t("cart.configuration.addons.summary.emptyTitle")}
            </p>
            <p className="max-w-xs text-brand-grey5 text-sm">
              {emptyDescription}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      {showHeader && (
        <div className="flex w-full items-center justify-between gap-4">
          <SelectedHeading count={items.length} />
          <button
            className="flex shrink-0 items-center gap-1.5 font-medium text-brand-green2-300 underline underline-offset-4"
            onClick={onRemoveAll}
            type="button"
          >
            <IconTrash className="size-6" />
            {t("cart.configuration.addons.summary.removeAll")}
          </button>
        </div>
      )}

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <li key={item.addon.id}>
            <DietAddonSelectedItem
              cateringName={cateringName}
              item={item}
              onEdit={() => onEdit(item.addon.id)}
              onRemove={() => onRemove(item.addon.id)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};
